import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { utils, writeFile } from 'xlsx';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../utils';
import { formatDate } from '../../utils/functions';
import Add from './add';
import Update from './update';
import axios from 'axios';
import { useSelector } from 'react-redux';

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const isActive = (item) => Number(item.is_active) === 1;

const statusBadgeClass = (item) =>
  isActive(item) ? 'duo-badge-pill duo-badge-pill--ok' : 'duo-badge-pill duo-badge-pill--off';

const statusLabel = (item) => (isActive(item) ? 'Active' : 'Inactive');

export default function Warehouses() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ search: '', date: '', status: '' });
  const [draftFilters, setDraftFilters] = useState({ date: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/warehouses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des magasins :', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, search: searchInput }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = useMemo(() => {
    return (data || []).filter((item) => {
      const search = appliedFilters.search.trim().toLowerCase();
      if (search) {
        const haystack = `${item.name || ''} ${item.code || ''} ${item.location || ''} ${item.manager || ''}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (appliedFilters.status !== '') {
        const active = isActive(item) ? '1' : '0';
        if (active !== appliedFilters.status) return false;
      }
      if (appliedFilters.date && item.created_at) {
        if (new Date(item.created_at).toDateString() !== new Date(appliedFilters.date).toDateString()) {
          return false;
        }
      }
      return true;
    });
  }, [data, appliedFilters]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = a[key] ?? '';
      const bValue = b[key] ?? '';
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
      active: filteredItems.filter(isActive).length,
      inactive: filteredItems.filter((i) => !isActive(i)).length,
    }),
    [filteredItems]
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / perPage));
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      date: draftFilters.date,
      status: draftFilters.status,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters({ date: '', status: '' });
    setAppliedFilters({ search: '', date: '', status: '' });
    setCurrentPage(1);
    setSortConfig({ key: 'name', direction: 'asc' });
  };

  const handleRefresh = async () => {
    await fetchData();
    setCurrentPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Magasins', 14, 16);
    doc.autoTable({
      head: [['Magasin', 'Code', 'Localisation', 'Manager', 'Capacité', 'Créée le', 'Statut']],
      body: sortedItems.map((item) => [
        item.name,
        item.code,
        item.location,
        item.manager,
        item.capacity,
        formatDate(item.created_at),
        statusLabel(item),
      ]),
      startY: 24,
    });
    doc.save(`Magasins ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheet = utils.json_to_sheet(
      sortedItems.map((item) => ({
        Magasin: item.name,
        Code: item.code,
        Localisation: item.location,
        Manager: item.manager,
        Capacité: item.capacity,
        'Créée le': formatDate(item.created_at),
        Statut: statusLabel(item),
      }))
    );
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Magasins');
    writeFile(workbook, `Magasins ${APP_NAME}.xlsx`);
  };

  const colSpan = isRole13 ? 7 : 8;

  return (
    <MainLayout loading={isLoading}>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Logistique</p>
          <h1 className="duo-hero-title">Magasins</h1>
          <p className="duo-hero-sub mt-2 mb-0">Gérez vos entrepôts et points de stockage.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Actifs</p>
            <p className="duo-kpi-chip__value">{stats.active}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Inactifs</p>
            <p className="duo-kpi-chip__value">{stats.inactive}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des magasins
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={() => window.print()} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <button type="button" className="duo-btn duo-btn--primary" onClick={() => setShowAddModal(true)}>
                  <i className="bi bi-plus" /> Nouveau
                </button>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher par nom, code, localisation..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de création</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.date}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Statut</label>
                    <select
                      className="duo-select"
                      value={draftFilters.status}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="">Tous les statuts</option>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('name')}>
                    Magasin <SortIcon active={isSortActive('name')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('location')}>
                    Localisation <SortIcon active={isSortActive('location')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('manager')}>
                    Manager <SortIcon active={isSortActive('manager')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('capacity')}>
                    Capacité <SortIcon active={isSortActive('capacity')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Créée le <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('is_active')}>
                    Statut <SortIcon active={isSortActive('is_active')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className="fw-bold d-block">{item.name}</span>
                        <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                          {item.code}
                        </span>
                      </td>
                      <td>{item.location || '—'}</td>
                      <td>{item.manager || '—'}</td>
                      <td>{item.capacity ?? '—'}</td>
                      <td className="text-nowrap">{formatDate(item.created_at)}</td>
                      <td>
                        <span className={statusBadgeClass(item)}>{statusLabel(item)}</span>
                      </td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Modifier"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => setEditingItem(item)}
                            >
                              <i className="bi bi-pencil-square" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucun magasin correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      <Add open={showAddModal} onClose={() => setShowAddModal(false)} refreshData={handleRefresh} />
      <Update
        open={!!editingItem}
        onClose={() => setEditingItem(null)}
        item={editingItem}
        refreshData={handleRefresh}
      />
    </MainLayout>
  );
}
